(() => {
  if (window.PmProductSystemInitialized) return;
  window.PmProductSystemInitialized = true;

  const stickySelector = '[data-pm-sticky-atc]';
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  let accordionIndex = 0;

  const cleanText = (value) => (value || '').replace(/\s+/g, ' ').trim();

  const setText = (element, value) => {
    if (!element || cleanText(element.textContent) === value) return;
    element.textContent = value;
  };

  const isVisible = (element) => {
    if (!element || !element.isConnected) return false;
    const styles = window.getComputedStyle(element);
    return styles.display !== 'none' && styles.visibility !== 'hidden' && element.getClientRects().length > 0;
  };

  const getPriceText = (productInfo) => {
    const priceRoot = productInfo.querySelector('[id^="price-"]');
    if (!priceRoot) return '';

    const candidates = Array.from(
      priceRoot.querySelectorAll('.price-item--sale, .price__regular .price-item--regular, .price-item--regular')
    );
    const price = candidates.find(isVisible) || candidates[0];
    return cleanText(price?.textContent);
  };

  const formatPriceText = (price, priceVaries) => {
    const normalizedPrice = cleanText(price).replace(/^(ab|von)\s+/i, '');
    if (!normalizedPrice) return '';
    return `${priceVaries ? 'ab ' : ''}${normalizedPrice}`;
  };

  const normalizeEmbeddedHeading = (productInfo) => {
    if (
      !productInfo ||
      (!productInfo.dataset.originalSection && !productInfo.closest('quick-add-modal'))
    ) {
      return;
    }

    const heading = productInfo.querySelector('.pm-product-heading__title');
    if (!heading || heading.tagName !== 'H1') return;

    const replacement = document.createElement('h2');
    Array.from(heading.attributes).forEach(({ name, value }) => replacement.setAttribute(name, value));
    while (heading.firstChild) replacement.append(heading.firstChild);
    heading.replaceWith(replacement);
  };

  const findInventorySource = (productInfo) => {
    const container = productInfo.querySelector('.product__info-container');
    if (!container) return null;

    return Array.from(container.children).find((element) => {
      if (element.matches('.pm-product-commerce-source') || element.querySelector('#sternify-bundle')) return false;
      const text = cleanText(element.textContent);
      return text.length <= 140 && /(Auf Lager|Ausverkauft|Nicht verfügbar|Lieferzeit)/i.test(text);
    });
  };

  const syncMobileCommerce = (productInfo) => {
    const commerce = productInfo.querySelector('[data-pm-mobile-commerce]');
    if (!commerce) return;

    const mobilePrice = commerce.querySelector('[data-pm-mobile-price]');
    const priceVaries = commerce.dataset.priceVaries === 'true';
    const currentPrice = formatPriceText(getPriceText(productInfo), priceVaries);
    if (currentPrice) setText(mobilePrice, currentPrice);

    const inventorySource = findInventorySource(productInfo);
    if (inventorySource && inventorySource.dataset.pmMobileInventorySource !== 'true') {
      inventorySource.dataset.pmMobileInventorySource = 'true';
    }

    const sourceText = cleanText(inventorySource?.textContent);
    const mainButton = productInfo.querySelector('.product-form__submit');
    const mainButtonDisabled = Boolean(
      mainButton && (mainButton.disabled || mainButton.getAttribute('aria-disabled') === 'true')
    );
    const sourceSaysUnavailable = /(Ausverkauft|Nicht verfügbar|nicht lieferbar)/i.test(sourceText);
    const sourceSaysAvailable = /(Auf Lager|Lieferzeit)/i.test(sourceText);

    let available = commerce.dataset.available === 'true';
    if (mainButton) available = !mainButtonDisabled;
    if (sourceSaysUnavailable) available = false;
    if (sourceSaysAvailable && !mainButtonDisabled) available = true;

    const availability = commerce.querySelector('[data-pm-mobile-availability]');
    const availabilityText = commerce.querySelector('[data-pm-mobile-availability-text]');
    const fallbackText = available ? 'Auf Lager · 1–3 Tage Lieferzeit' : 'Ausverkauft';

    commerce.dataset.available = String(available);
    availability?.classList.toggle('is-unavailable', !available);
    setText(availabilityText, sourceText || fallbackText);
  };

  const initMobileCommerce = (productInfo) => {
    if (!productInfo?.querySelector('[data-pm-mobile-commerce]')) return;
    if (productInfo.dataset.pmMobileCommerceReady === 'true') {
      syncMobileCommerce(productInfo);
      return;
    }

    productInfo.dataset.pmMobileCommerceReady = 'true';
    let syncFrame;
    const scheduleSync = () => {
      if (syncFrame) return;
      syncFrame = window.requestAnimationFrame(() => {
        syncFrame = undefined;
        syncMobileCommerce(productInfo);
      });
    };

    new MutationObserver(scheduleSync).observe(productInfo, {
      attributes: true,
      attributeFilter: ['class', 'disabled', 'aria-disabled'],
      childList: true,
      characterData: true,
      subtree: true,
    });
    productInfo.addEventListener('change', scheduleSync);
    syncMobileCommerce(productInfo);
  };

  const initAccordion = (details) => {
    if (details.dataset.pmAccordionReady === 'true') return;
    const summary = details.querySelector(':scope > summary');
    const content = details.querySelector(':scope > .pm-product-details__content');
    if (!summary || !content) return;

    details.dataset.pmAccordionReady = 'true';
    accordionIndex += 1;
    if (!content.id) content.id = `PmProductAccordion-${accordionIndex}`;
    summary.setAttribute('aria-controls', content.id);
    summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');

    details.addEventListener('toggle', () => {
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    });

    summary.addEventListener('click', (event) => {
      if (reducedMotionQuery.matches || typeof content.animate !== 'function') return;
      event.preventDefault();
      if (details.classList.contains('is-animating')) return;

      const isClosing = details.open;
      if (!isClosing) details.open = true;

      details.classList.add('is-animating');
      summary.setAttribute('aria-expanded', isClosing ? 'false' : 'true');
      const startHeight = isClosing ? content.getBoundingClientRect().height : 0;
      const endHeight = isClosing ? 0 : content.scrollHeight;
      const animation = content.animate(
        [
          { height: `${startHeight}px`, opacity: isClosing ? 1 : 0 },
          { height: `${endHeight}px`, opacity: isClosing ? 0 : 1 },
        ],
        { duration: 180, easing: 'ease' }
      );

      animation.addEventListener('finish', () => {
        if (isClosing) details.open = false;
        details.classList.remove('is-animating');
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      });
      animation.addEventListener('cancel', () => details.classList.remove('is-animating'));
    });
  };

  const initAccordions = (scope = document) => {
    const accordions = [];
    if (scope.matches?.('.pm-product-details__row')) accordions.push(scope);
    accordions.push(...scope.querySelectorAll('.pm-product-details__row'));
    accordions.forEach(initAccordion);
  };

  let faqListIndex = 0;

  const initFaqList = (list) => {
    if (list.dataset.pmFaqReady === 'true') return;
    list.dataset.pmFaqReady = 'true';

    const rows = Array.from(list.querySelectorAll(':scope > .pm-product-faq__row'));
    const hiddenRows = rows.slice(4);
    if (!hiddenRows.length) return;

    hiddenRows.forEach((row) => row.classList.add('pm-product-faq__row--initially-hidden'));
    if (!list.id) list.id = `PmProductFaqList-${++faqListIndex}`;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pm-product-faq__more';
    button.setAttribute('aria-controls', list.id);
    button.setAttribute('aria-expanded', 'false');
    button.textContent = `${hiddenRows.length} weitere Fragen anzeigen`;

    button.addEventListener('click', () => {
      const willExpand = button.getAttribute('aria-expanded') !== 'true';
      hiddenRows.forEach((row) => row.classList.toggle('pm-product-faq__row--initially-hidden', !willExpand));
      button.setAttribute('aria-expanded', willExpand ? 'true' : 'false');
      button.textContent = willExpand ? 'Weniger Fragen anzeigen' : `${hiddenRows.length} weitere Fragen anzeigen`;
    });

    list.insertAdjacentElement('afterend', button);
  };

  const initFaqLists = (scope = document) => {
    const lists = [];
    if (scope.matches?.('.pm-product-faq__list')) lists.push(scope);
    lists.push(...scope.querySelectorAll('.pm-product-faq__list'));
    lists.forEach(initFaqList);
  };

  const initStickyAtc = (sticky) => {
    if (sticky.dataset.pmReady === 'true') return;
    sticky.dataset.pmReady = 'true';

    const productInfo = sticky.closest('product-info');
    const stickyButton = sticky.querySelector('[data-pm-sticky-button]');
    const stickyLabel = sticky.querySelector('[data-pm-sticky-label]');
    const stickyPrice = sticky.querySelector('[data-pm-sticky-price]');
    const form = document.getElementById(sticky.dataset.productFormId);
    const formDomId = form?.getAttribute('id');
    let mainButton = form?.querySelector('.product-form__submit');
    let observedBlockingTargets = [];
    let blockingTargetIsVisible = false;
    let intersectionObserver;
    let hideTimer;

    if (
      !productInfo ||
      productInfo.dataset.originalSection ||
      sticky.closest('quick-add-modal') ||
      !stickyButton ||
      !stickyLabel ||
      !stickyPrice ||
      !mainButton
    ) {
      sticky.hidden = true;
      return;
    }

    document.documentElement.classList.add('pm-has-mobile-sticky-atc');
    sticky.setAttribute('aria-hidden', 'true');

    const getBundle = () => {
      const bundle = productInfo.querySelector('#sternify-bundle');
      return isVisible(bundle) ? bundle : null;
    };

    const syncEnvironmentOffset = () => {
      const previewBar = document.getElementById('PBarNextFrameWrapper');
      const previewBarHeight = previewBar ? Math.ceil(previewBar.getBoundingClientRect().height) : 0;

      if (previewBarHeight > 0) {
        sticky.style.setProperty('--pm-sticky-bottom-offset', `${previewBarHeight}px`);
      } else {
        sticky.style.removeProperty('--pm-sticky-bottom-offset');
      }
    };

    const setStickyVisibility = () => {
      syncEnvironmentOffset();
      const shouldShow = mobileQuery.matches && !blockingTargetIsVisible;

      if (shouldShow) {
        window.clearTimeout(hideTimer);
        sticky.hidden = false;
        sticky.inert = false;
        sticky.setAttribute('aria-hidden', 'false');
        window.requestAnimationFrame(() => sticky.classList.add('is-visible'));
        return;
      }

      sticky.classList.remove('is-visible');
      sticky.inert = true;
      sticky.setAttribute('aria-hidden', 'true');
      window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(() => {
        if (!sticky.classList.contains('is-visible')) sticky.hidden = true;
      }, reducedMotionQuery.matches ? 0 : 190);
    };

    const observeBlockingTargets = () => {
      const nextButton = form.querySelector('.product-form__submit') || mainButton;
      if (!nextButton) return;

      mainButton = nextButton;
      const targets = [
        mainButton,
        ...document.querySelectorAll('.pm-product-recommendations .quick-add__submit'),
      ];
      const targetsUnchanged =
        targets.length === observedBlockingTargets.length &&
        targets.every((target, index) => target === observedBlockingTargets[index]);
      if (targetsUnchanged) return;

      observedBlockingTargets = targets;
      const visibleTargets = new Set();
      blockingTargetIsVisible = false;
      intersectionObserver?.disconnect();
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              visibleTargets.add(entry.target);
            } else {
              visibleTargets.delete(entry.target);
            }
          });
          blockingTargetIsVisible = visibleTargets.size > 0;
          setStickyVisibility();
        },
        { threshold: 0.01 }
      );
      targets.forEach((target) => intersectionObserver.observe(target));
    };

    const syncContent = () => {
      observeBlockingTargets();
      const bundle = getBundle();
      const bundleReady = bundle?.dataset.pmBundleSelectionReady === 'true';
      const mainButtonDisabled = mainButton.disabled || mainButton.getAttribute('aria-disabled') === 'true';

      if (bundle && !bundleReady) {
        setText(stickyLabel, 'Set wählen');
        stickyButton.disabled = false;
        stickyButton.setAttribute('aria-controls', bundle.id);
      } else if (bundleReady) {
        setText(stickyLabel, 'In den Warenkorb');
        stickyButton.disabled = mainButtonDisabled;
        stickyButton.setAttribute('aria-controls', formDomId);
      } else {
        const mainLabel = cleanText(mainButton.querySelector('span')?.textContent);
        if (mainLabel) setText(stickyLabel, mainLabel);
        stickyButton.disabled = mainButtonDisabled;
        stickyButton.setAttribute('aria-controls', formDomId);
      }

      const commerce = productInfo.querySelector('[data-pm-mobile-commerce]');
      const currentPrice = formatPriceText(getPriceText(productInfo), commerce?.dataset.priceVaries === 'true');
      if (currentPrice) setText(stickyPrice, currentPrice);
      setStickyVisibility();
    };

    stickyButton.addEventListener('click', () => {
      const bundle = getBundle();
      const bundleReady = bundle?.dataset.pmBundleSelectionReady === 'true';

      if (bundle && !bundleReady) {
        const headerOffset = 96;
        const bundleTop = bundle.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({ top: Math.max(0, bundleTop), behavior: reducedMotionQuery.matches ? 'auto' : 'smooth' });
        window.setTimeout(
          () => {
            const firstControl = Array.from(
              bundle.querySelectorAll(
                'select:not([disabled]), input:not([disabled]), button:not([disabled]), [role="button"][tabindex="0"]'
              )
            ).find(isVisible);
            firstControl?.focus({ preventScroll: true });
          },
          reducedMotionQuery.matches ? 0 : 500
        );
        return;
      }

      if (!mainButton.disabled && mainButton.getAttribute('aria-disabled') !== 'true') {
        mainButton.click();
      }
    });

    new MutationObserver((mutations) => {
      const hasRelevantChange = mutations.some((mutation) => !mutation.target.closest(stickySelector));
      if (hasRelevantChange) syncContent();
    }).observe(productInfo, {
      attributes: true,
      attributeFilter: ['class', 'disabled', 'aria-disabled', 'data-pm-bundle-selection-ready'],
      childList: true,
      subtree: true,
    });

    productInfo.addEventListener('pm:bundle-selection-change', syncContent);
    document.addEventListener('product-recommendations:loaded', syncContent);
    mobileQuery.addEventListener?.('change', setStickyVisibility);
    window.addEventListener('resize', syncEnvironmentOffset, { passive: true });
    observeBlockingTargets();
    syncContent();
  };

  const initProductInfo = (productInfo) => {
    normalizeEmbeddedHeading(productInfo);
    initMobileCommerce(productInfo);
  };

  const initAllProductInfo = (scope = document) => {
    const productInfos = [];
    if (scope.matches?.('product-info')) productInfos.push(scope);
    productInfos.push(...scope.querySelectorAll('product-info'));
    productInfos.forEach(initProductInfo);
  };

  const initAllStickyAtc = (scope = document) => {
    const stickyBars = [];
    if (scope.matches?.(stickySelector)) stickyBars.push(scope);
    stickyBars.push(...scope.querySelectorAll(stickySelector));
    stickyBars.forEach(initStickyAtc);
  };

  const syncRecommendationShell = (shell) => {
    const recommendations = shell.querySelector('product-recommendations');
    const hasProducts = Boolean(recommendations?.querySelector('.pm-product-recommendations__grid > li'));
    const hasLoaded = recommendations?.dataset.recommendationsLoaded === 'true' || hasProducts;

    shell.hidden = hasLoaded && !hasProducts;
    shell.setAttribute('aria-busy', hasLoaded ? 'false' : 'true');
  };

  const syncRecommendationShells = (scope = document) => {
    scope.querySelectorAll('[data-pm-recommendations-shell]').forEach(syncRecommendationShell);
  };

  const init = (scope = document) => {
    initAllProductInfo(scope);
    initAllStickyAtc(scope);
    initAccordions(scope);
    initFaqLists(scope);
    syncRecommendationShells(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  const recommendationsObserver = new MutationObserver(() => syncRecommendationShells());
  recommendationsObserver.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('product-info:loaded', (event) => init(event.target));
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('product-recommendations:loaded', (event) => {
    const shell = event.target.closest('[data-pm-recommendations-shell]');
    if (shell) syncRecommendationShell(shell);
  });
})();
